// Thin typed client over the Railyard FastAPI server.

import { firebaseAuth } from "@/lib/firebase";
import { DEV_AUTH_EMAIL, DEV_AUTH_ENABLED, DEV_AUTH_TENANT_ID } from "@/lib/dev-auth";
import type { components } from "@/lib/api-types";
import { logger } from "@/lib/log";

const log = logger("api");

/** Convenience alias — every server schema lives under components["schemas"]. */
type Schemas = components["schemas"];

export const API = process.env.NEXT_PUBLIC_API ?? "http://127.0.0.1:8000";

/** Base class for every error originating from a server response. Carries
 * enough structured context for callers to discriminate (status, url) and
 * for debugging (body, detail), so we never have to grep error messages. */
export class ApiError extends Error {
  status: number;
  url: string;
  detail: string;
  body: unknown;
  constructor(opts: { status: number; url: string; detail: string; body: unknown }) {
    super(opts.detail);
    this.name = "ApiError";
    this.status = opts.status;
    this.url = opts.url;
    this.detail = opts.detail;
    this.body = opts.body;
  }
}

/** Thrown when the server rejects our Firebase ID token after a refresh
 * attempt. Extends ApiError so generic `instanceof ApiError` handlers see
 * it too, but the dedicated subclass lets the UI route to a sign-in
 * recovery path instead of a generic "retry". */
export class AuthError extends ApiError {
  constructor(status: number, detail: string, url = "", body: unknown = null) {
    super({ status, url, detail, body });
    this.name = "AuthError";
  }
}

/** Best user-facing message for a caught unknown: ApiError (and its
 * AuthError subclass) carry the server-provided `detail`; any other Error
 * contributes its message; anything else resolves to the caller's fallback.
 * Centralizes the `e instanceof Error ? e.message : fallback` ternary that
 * used to be copy-pasted at every catch site (see also `toastError` in
 * contexts/toast-context). */
export function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.detail || fallback;
  if (err instanceof Error) return err.message || fallback;
  return fallback;
}

/** Parse a non-2xx response body. Tries JSON first (FastAPI's
 * `{ detail: ... }` shape, flattening string/object/HTTPException forms),
 * falls back to text, finally falls back to `HTTP {status}`. Never throws —
 * a malformed body must not mask the original failure. */
export async function readErrorBody(r: Response): Promise<{ detail: string; body: unknown }> {
  let body: unknown = null;
  let detail = `HTTP ${r.status}`;
  try {
    const cloned = r.clone();
    body = await cloned.json();
    const d = (body as { detail?: unknown })?.detail;
    if (typeof d === "string") {
      detail = d;
    } else if (d && typeof d === "object") {
      const dm = (d as { message?: unknown }).message;
      detail = typeof dm === "string" ? dm : JSON.stringify(d);
    }
  } catch {
    try {
      const text = await r.text();
      if (text) {
        detail = text;
        body = text;
      }
    } catch {
      /* leave the HTTP {status} fallback in place */
    }
  }
  return { detail, body };
}

/** Throwing wrapper over `apiFetch`. Turns any non-2xx (after the 401
 * refresh dance in apiFetch) into a typed `ApiError`. Pass
 * `allowStatus` to opt specific codes (e.g. 404, 204) back into the
 * "success" path so the caller can treat them as idempotent. */
export async function requestOk(
  input: string,
  init?: RequestInit,
  opts?: {
    allowStatus?: number[];
    retryOn401?: boolean;
    preparedHeaders?: Headers;
    tenantRecoveryProbe?: boolean;
  },
): Promise<Response> {
  const r = await apiFetch(input, init, {
    retryOn401: opts?.retryOn401,
    preparedHeaders: opts?.preparedHeaders,
    tenantRecoveryProbe: opts?.tenantRecoveryProbe,
  });
  if (r.ok) return r;
  if (opts?.allowStatus?.includes(r.status)) return r;
  const { detail, body } = await readErrorBody(r);
  log.warn("api request failed", { url: input, method: init?.method ?? "GET", status: r.status, detail });
  throw new ApiError({ status: r.status, url: input, detail, body });
}

/** Throwing wrapper that returns the parsed JSON body on success. */
export async function requestJson<T>(
  input: string,
  init?: RequestInit,
  opts?: { retryOn401?: boolean; preparedHeaders?: Headers; tenantRecoveryProbe?: boolean },
): Promise<T> {
  const r = await requestOk(input, init, opts);
  return r.json() as Promise<T>;
}

// Staff impersonation token. Held in-memory only — never persisted to
// localStorage. A page reload ends the impersonation session, which is
// the right default (impersonation should be an intentional act).
let _impersonationToken: string | null = null;

// Identifies the current in-memory impersonation session (ENG-4155). Minted
// with the token and discarded with it, so it is non-null only while THIS JS
// context holds a live token. The durable active-workspace pin is stamped with
// it, which is what lets a reader tell "this pin belongs to the impersonation
// session I am still in" from "this pin outlived the token that justified it".
let _impersonationSessionId: string | null = null;

// Module-private on purpose: every read and write goes through the accessors
// below, so the stamping invariant holds by construction rather than by every
// call site remembering it.
/**
 * Key holding the active workspace pin (`X-Active-Workspace`). Lives in
 * `localStorage` for an ordinary workspace choice and in `sessionStorage` for
 * an impersonation-scoped one -- see `readActiveWorkspacePin`.
 */
const ACTIVE_WORKSPACE_KEY = "active_workspace_tenant_id";

/**
 * Key stamping the pin as belonging to an impersonation session. Written only
 * for pins set while impersonating, into that tab's `sessionStorage` alongside
 * the pin itself; its value is the session id that wrote it.
 *
 * ENG-4155: the two halves of an impersonation session used to have mismatched
 * lifetimes — the token was memory-only, the pin durable. When iOS tore down
 * the JS context (background-tab discard, in-app-browser → Safari handoff) the
 * browser kept sending `X-Active-Workspace: <customer tenant>` with no
 * `X-Impersonate` — i.e. as the real staff user, who is not a member of that
 * tenant — so `_resolve_active_workspace` failed closed with 403
 * WORKSPACE_ACCESS_DENIED and the app rendered an empty task list and a blank
 * thread. The stamp makes the pin exactly as ephemeral as the token: no live
 * session id, no pin.
 */
const ACTIVE_WORKSPACE_IMPERSONATION_KEY = "active_workspace_impersonation_sid";

function newImpersonationSessionId(): string {
  const c = globalThis.crypto;
  if (typeof c?.randomUUID === "function") return c.randomUUID();
  return `imp-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

export function setImpersonationToken(token: string | null): void {
  _impersonationToken = token;
  _impersonationSessionId = token ? newImpersonationSessionId() : null;
  // Ending the session orphans the pin that belonged to it. The staff user's own
  // localStorage choice is deliberately left alone — it is theirs, not the
  // impersonated session's, and it should still be there afterwards.
  if (!token) clearImpersonationWorkspacePin();
}

/** True while this JS context holds a live impersonation token. */
export function hasLiveImpersonation(): boolean {
  return _impersonationToken !== null;
}

/**
 * Notified when the transport drops a workspace pin mid-session *without* a
 * reload — today, the impersonated `WORKSPACE_ACCESS_DENIED` recovery.
 *
 * The reload the non-impersonated path does is also what resynchronises the
 * app; skipping it (because it would end the impersonation session) leaves
 * `AuthProvider`'s `org` and tenant-scoped caches describing a workspace the
 * wire has already moved off. A background poll can trigger that while the
 * operator is looking at fully-rendered customer UI, so a later click could
 * issue a mutation against a tenant the screen never showed (ENG-4316).
 *
 * The transport only announces; `AuthProvider` owns the re-resolution, so cache
 * knowledge stays out of this layer. The handler may be async, and the recovery
 * waits for it: releasing the retry first would hand the caller home-tenant data
 * while the screen still showed the customer tenant, and a click in that window
 * would write to a tenant the user never saw (codex P1).
 *
 * Also raised by an in-place workspace switch during impersonation, where a
 * reload is not available to do the resynchronising.
 */
let _onTenantContextChanged: (() => void | Promise<void>) | null = null;

export function setTenantContextChangedHandler(
  handler: (() => void | Promise<void>) | null,
): void {
  _onTenantContextChanged = handler;
}

/**
 * The re-resolution currently in flight, if any. Single-flighted so concurrent
 * callers join one recovery rather than stacking several, and so mutations can
 * be held behind it.
 */
let _tenantRecovery: Promise<void> | null = null;

let _tenantRecoveryGen = 0;

/**
 * Latched when a re-resolution fails and cleared only when one succeeds.
 *
 * Without it the refusal lasts only as long as the failed attempt: once that
 * promise settles the slot is empty again, so the NEXT mutation sees no
 * recovery in flight and goes out pinless, against a tenant nothing has
 * confirmed (codex P1). The latch keeps the session failed-closed until the
 * tenant is actually known again.
 */
let _tenantUnresolved = false;

/**
 * Bumped on every change to the pin. A re-resolution resolves one specific
 * version, so a change that lands while one is already in flight must not be
 * satisfied by joining it — that older run is resolving the previous pin
 * (codex P1).
 */
let _tenantVersion = 0;
let _recoveryVersion = -1;

/**
 * Re-resolve the app's tenant state, once, no matter how many callers ask.
 *
 * The returned promise REJECTS when re-resolution failed. That is deliberate:
 * the pin is already gone at this point, so a failure that resolved
 * successfully would release gated mutations against the home tenant while the
 * screen still described the previous one (codex P1). Failing closed keeps them
 * blocked until the tenant is actually known.
 */
export function notifyTenantContextChanged(): Promise<void> {
  const want = _tenantVersion;
  // Join an in-flight run only when it is already resolving at least this
  // version of the pin. Otherwise it is resolving a pin we have since replaced,
  // and joining it would report the wrong tenant as settled.
  if (_tenantRecovery && _recoveryVersion >= want) return _tenantRecovery;

  // A generation counter rather than comparing against the promise itself:
  // only the newest run may clear the slot, and it reads cleanly without
  // referring to a binding that is still being assigned.
  const gen = ++_tenantRecoveryGen;
  const previous = _tenantRecovery;
  _recoveryVersion = want;
  const run = (async () => {
    // Chain rather than race: two concurrent `/api/me` reads could otherwise
    // settle out of order and leave the older one's answer in place.
    if (previous) {
      try {
        await previous;
      } catch {
        /* its failure is its own; this run re-resolves from scratch */
      }
    }
    try {
      // Loop rather than settle once: the pin can move while the handler is
      // awaiting `/api/me`, and reporting the tenant settled at the version we
      // started from would leave the newer pin unresolved (codex P1). Bounded,
      // so a pathological churn cannot spin here forever.
      for (let round = 0; ; round++) {
        const at = _tenantVersion;
        await _onTenantContextChanged?.();
        _recoveryVersion = at;
        if (_tenantVersion === at || round >= 2) break;
        // A newer run has already been queued for the newer version — let it
        // own that, rather than resolving the same pin twice.
        if (_tenantRecoveryGen !== gen) break;
      }
      _tenantUnresolved = false;
    } catch (e) {
      _tenantUnresolved = true;
      throw e;
    } finally {
      if (_tenantRecoveryGen === gen) _tenantRecovery = null;
    }
  })();
  // Every waiter observes the rejection; this only stops Node/the browser
  // reporting it as unhandled when nobody happens to be waiting.
  run.catch(() => {});
  _tenantRecovery = run;
  return run;
}

/**
 * Hold a write until any tenant re-resolution has settled, throwing
 * `TenantContextUnresolvedError` if the tenant could not be confirmed.
 *
 * `apiFetch` applies this itself. It is exported for transports that do not go
 * through it — the multipart uploader builds its own headers and dispatches via
 * Axios, so without this an upload started mid-recovery would be created in the
 * wrong tenant (codex P1). Call it immediately before building headers, and
 * again after, since header preparation awaits Firebase.
 */
export function awaitTenantSettled(): Promise<void> {
  return awaitTenantRecovery();
}

/** True while a tenant re-resolution is in flight or has failed unrecovered. */
export function tenantContextInDoubt(): boolean {
  return _tenantRecovery !== null || _tenantUnresolved;
}

/**
 * Raised instead of re-sending a write whose tenant changed between attempts.
 *
 * A retry is only safe if it goes to the same place as the original. The user
 * chose this action against one workspace; silently re-issuing it against
 * another is a wrong-tenant write, so the retry is refused and the caller
 * re-decides (codex P1).
 */
export class TenantChangedError extends Error {
  constructor() {
    super("The active workspace changed while this was in flight. Try again.");
    this.name = "TenantChangedError";
  }
}

/** Raised instead of dispatching a mutation whose tenant we can no longer pin down. */
export class TenantContextUnresolvedError extends Error {
  constructor() {
    super("Your workspace access changed and could not be re-checked. Reload and try again.");
    this.name = "TenantContextUnresolvedError";
  }
}

// localStorage access is wrapped because it throws outright in some private /
// restricted-storage modes, and a workspace pin is never worth breaking a
// request over.
function lsGet(key: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Returns false when the write could not be persisted (private mode, quota). */
function lsSet(key: string, value: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

function lsRemove(key: string): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(key);
  } catch {
    /* private mode */
  }
}

// sessionStorage mirrors of the above. Same failure handling; separate store
// because it is scoped to one tab, which is what an impersonation session needs.
function ssGet(key: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function ssSet(key: string, value: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    sessionStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

function ssRemove(key: string): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* private mode */
  }
}

/**
 * The active workspace pin, as the wire is allowed to see it.
 *
 * Two stores, because the pin has two different owners:
 *
 * - An **impersonation** pin belongs to one JS context. It lives in
 *   `sessionStorage`, which is scoped to a single tab, alongside a stamp
 *   naming the session that wrote it. `sessionStorage` survives a same-tab
 *   reload but the in-memory token does not, so the stamp is what makes the
 *   pin die with the token (ENG-4155).
 * - An **ordinary** pin is a durable, origin-wide workspace choice and stays
 *   in `localStorage`, exactly as before.
 *
 * Splitting them is what makes the scopes honest: a session id is per-context,
 * so qualifying an origin-wide slot with one meant another tab's pin was
 * simultaneously visible and unusable — every sharp edge in review came from
 * that (ENG-4310). A foreign session's pin is now simply invisible, so nothing
 * can clobber it and nothing can inherit it.
 *
 * Inside an impersonation session this NEVER falls back to the `localStorage`
 * pin: that is the real staff user's own workspace choice, and sending it under
 * `X-Impersonate` would scope the impersonated user to a tenant they never
 * asked for.
 */
export function readActiveWorkspacePin(): string | null {
  if (_impersonationSessionId) {
    const pin = ssGet(ACTIVE_WORKSPACE_KEY);
    if (pin && ssGet(ACTIVE_WORKSPACE_IMPERSONATION_KEY) === _impersonationSessionId) {
      return pin;
    }
    return null;
  }
  // Not impersonating. Any session pin here belongs to a session that ended in
  // THIS tab (a reload killed the token), so sweeping it is safe -- unlike the
  // origin-wide store, nobody else can be relying on it.
  if (ssGet(ACTIVE_WORKSPACE_KEY) !== null) clearImpersonationWorkspacePin();
  return lsGet(ACTIVE_WORKSPACE_KEY);
}

/**
 * Does this tab already have a workspace pinned, by either store?
 *
 * Used by automatic, non-user-initiated writers (the default-workspace seeding
 * in app-shell) to stand down rather than overwrite a decision already made.
 * `readActiveWorkspacePin` answers the narrower "what may this context send",
 * and a seeding writer that conflated the two would seed straight over a pin
 * it merely could not use.
 */
export function hasAnyActiveWorkspacePin(): boolean {
  return ssGet(ACTIVE_WORKSPACE_KEY) !== null || lsGet(ACTIVE_WORKSPACE_KEY) !== null;
}

/**
 * Pin the active workspace, into whichever store owns it.
 *
 * While impersonating, the pin and its stamp go to this tab's `sessionStorage`
 * and the staff user's own `localStorage` pin is left untouched -- so it is
 * still there when the impersonation session ends.
 *
 * Returns false when the pin could not be persisted (private mode, quota).
 * Callers that reload to apply the pin must check it: reloading after a failed
 * write re-runs the same code against the same empty storage, which loops.
 */
export function setActiveWorkspacePin(tenantId: string): boolean {
  if (_impersonationSessionId) {
    if (!ssSet(ACTIVE_WORKSPACE_KEY, tenantId)) return false;
    // A pin that persists without its stamp would read as an ordinary choice
    // and outlive the token -- the ENG-4155 bug. Better to hold no pin at all
    // than the durable half of a broken pair.
    if (!ssSet(ACTIVE_WORKSPACE_IMPERSONATION_KEY, _impersonationSessionId)) {
      ssRemove(ACTIVE_WORKSPACE_KEY);
      return false;
    }
    _tenantVersion++;
    return true;
  }
  const ok = lsSet(ACTIVE_WORKSPACE_KEY, tenantId);
  if (ok) _tenantVersion++;
  return ok;
}

/**
 * Drop the pin from both stores. Returns true if a pin was actually removed.
 *
 * This is the "forget the workspace entirely" clear -- an explicit switch to no
 * workspace, and impersonation session setup/teardown, which must not leave a
 * customer tenant pinned for the real staff user.
 */
export function clearActiveWorkspacePin(): boolean {
  const had = hasAnyActiveWorkspacePin();
  ssRemove(ACTIVE_WORKSPACE_KEY);
  ssRemove(ACTIVE_WORKSPACE_IMPERSONATION_KEY);
  lsRemove(ACTIVE_WORKSPACE_KEY);
  if (had) _tenantVersion++;
  return had;
}

/**
 * Drop only the impersonation-scoped pin, leaving the staff user's own
 * `localStorage` choice alone. Returns true if a pin was actually removed.
 */
function clearImpersonationWorkspacePin(): boolean {
  const had = ssGet(ACTIVE_WORKSPACE_KEY) !== null;
  ssRemove(ACTIVE_WORKSPACE_KEY);
  ssRemove(ACTIVE_WORKSPACE_IMPERSONATION_KEY);
  if (had) _tenantVersion++;
  return had;
}

/**
 * Drop the impersonation pin only if it is still the one `expected` — the pin
 * the denied request actually carried.
 *
 * A denial is evidence about the workspace that request was sent with, not
 * about whatever is pinned by the time the response arrives. Clearing blind
 * would let a slow denial for workspace A wipe the pin for workspace B that the
 * operator has since switched to (codex P1).
 */
function clearImpersonationWorkspacePinIfCurrent(expected: {
  pin: string | null;
  session: string | null;
}): boolean {
  if (expected.pin === null) return false;
  // The session must match too: an in-place switch from one impersonation
  // target to another can leave a request in flight for the old one, and if
  // both happen to pin the same workspace a value-only check would let the old
  // target's denial clear the new target's valid pin (codex P2).
  if (expected.session !== _impersonationSessionId) {
    log.debug("workspace denial belongs to a previous impersonation session");
    return false;
  }
  if (ssGet(ACTIVE_WORKSPACE_KEY) !== expected.pin) {
    log.debug("workspace denial is stale — the pin already moved on", { pin: expected.pin });
    return false;
  }
  return clearImpersonationWorkspacePin();
}

/**
 * A stable string identifying the current auth/tenant principal — the same
 * dimensions the backend varies preview responses on (`Authorization` /
 * `X-Active-Workspace` / `X-Impersonate`). Caches that key blobs by URL alone
 * (e.g. the deck page cache) must fold this in so switching account, workspace,
 * or impersonation in one browser can't serve a colliding URL's bytes to a
 * different principal without a fresh, ACL-checked request.
 */
export function authScopeKey(): string {
  // `readActiveWorkspacePin`, not the raw key: an orphaned impersonation pin is
  // not sent on the wire, so it must not vary the cache key either.
  const workspace = readActiveWorkspacePin() ?? "";
  const user = DEV_AUTH_ENABLED ? DEV_AUTH_EMAIL : firebaseAuth.currentUser?.uid ?? "";
  return `${user}|${workspace}|${_impersonationToken ?? ""}`;
}

// Only bootstrap-like endpoints stay exempt from the X-Active-Workspace header:
// they must run in the user's home org context. `/api/me` and `/api/org/*` are
// intentionally NOT exempt so the Organization surface follows the workspace
// switcher.
const WORKSPACE_EXEMPT_PREFIXES = [
  "/api/me/bootstrap",
  "/api/me/accept-workspace-invite",
  "/api/workspace-invites/",
];
const WORKSPACE_EXEMPT_EXACT = new Set<string>();

function _isWorkspaceExempt(urlOrPath: string): boolean {
  let path: string;
  try {
    path = new URL(urlOrPath).pathname;
  } catch {
    path = urlOrPath;
  }
  if (WORKSPACE_EXEMPT_EXACT.has(path)) return true;
  return WORKSPACE_EXEMPT_PREFIXES.some(p => path.startsWith(p));
}

// Build the auth + workspace headers every request carries. Factored out of
// apiFetch so the progress-capable XHR uploader (uploads.ts) reuses the exact
// same auth (dev-auth headers OR Firebase bearer, impersonation, active
// workspace) instead of re-deriving it and drifting.
export async function authHeaders(
  input: string,
  init: RequestInit = {},
  forceRefresh = false,
): Promise<Headers> {
  const headers = new Headers(init.headers as HeadersInit);
  if (DEV_AUTH_ENABLED) {
    // Hermetic dev/test mode: authenticate via the backend's header-auth
    // path (RAILYARD_ALLOW_HEADER_AUTH=1) instead of a Firebase token.
    // `X-Dev-User` resolves a real, persisted owner account so admin-gated
    // surfaces are reachable for local QA.
    headers.set("X-Tenant-Id", DEV_AUTH_TENANT_ID);
    headers.set("X-Dev-User", DEV_AUTH_EMAIL);
  } else {
    // Wait for Firebase to finish determining the initial auth state before
    // trusting `currentUser`. Without this, a request fired right as the SDK
    // is still restoring a persisted session (slow mobile network, a cold
    // webview boot, a page resumed from bfcache) reads `currentUser` as null
    // and silently goes out with no Authorization header — a 401 the caller
    // can't distinguish from a genuinely signed-out user. Resolves
    // immediately once the SDK has already settled (the common case).
    await firebaseAuth.authStateReady();
    const token = await firebaseAuth.currentUser?.getIdToken(forceRefresh);
    if (token) headers.set("Authorization", `Bearer ${token}`);
  }
  if (_impersonationToken) headers.set("X-Impersonate", _impersonationToken);
  // An impersonation-stamped pin whose session is gone resolves to null here,
  // so a torn-down context never sends a customer workspace as the real staff
  // user — the ENG-4155 failure.
  const activeWorkspace = readActiveWorkspacePin();
  if (activeWorkspace && !_isWorkspaceExempt(input)) {
    headers.set("X-Active-Workspace", activeWorkspace);
  }
  return headers;
}

export async function apiFetch(
  input: string,
  init: RequestInit = {},
  opts: {
    retryOn401?: boolean;
    preparedHeaders?: Headers;
    /**
     * Marks THIS request as the tenant re-resolution's own `/api/me`. Only such
     * a request may skip raising a new resolution when it is denied — joining
     * the resolution that is waiting on it would hang both. Suppressing on a
     * global "a recovery is running" flag instead would also silence unrelated
     * requests that merely overlapped it, losing the follow-up they needed
     * (codex P1).
     */
    tenantRecoveryProbe?: boolean;
  } = {},
): Promise<Response> {
  // `ignorePrepared` re-derives headers without forcing a token refresh — the
  // WORKSPACE_ACCESS_DENIED retry needs fresh headers (the prepared ones still
  // carry the pin it just dropped) but the credential itself is fine.
  // Only side-effect-free methods may cross a tenant recovery. Computed once:
  // `send` consults it on every attempt.
  const gated = !SIDE_EFFECT_FREE_METHODS.has((init.method ?? "GET").toUpperCase());
  // What the most recent attempt actually sent. A denial is evidence about THAT
  // pin under THAT session, not about whatever is pinned when the response
  // lands — and the workspace id alone is not enough to identify it, since two
  // impersonation targets can pin the same workspace (codex P2).
  let sentWith: { pin: string | null; session: string | null } = { pin: null, session: null };

  const send = async (
    forceRefresh: boolean,
    ignorePrepared = false,
    requireIdentity?: { pin: string | null; session: string | null },
  ): Promise<Response> => {
    // `preparedHeaders` lets a caller acquire auth BEFORE it commits to the
    // request, so nothing between its decision and the wire touches the
    // network. Without it this lookup is the last thing before `fetch`, which
    // is fine for callers whose body doesn't encode a time-sensitive verdict
    // and wrong for one that does. A forced refresh re-derives regardless —
    // prepared credentials are precisely what a 401 just rejected.
    // A mutation must not slip out between the pin being dropped and the app
    // re-resolving: it would carry no workspace and land in the home tenant
    // while the screen still described the customer's. Reads are deliberately
    // NOT gated — the recovery's own `/api/me` and the revalidations it
    // triggers are reads, and holding them would deadlock on the recovery they
    // are part of.
    //
    // The check is repeated AFTER the headers are built, because `authHeaders`
    // awaits Firebase: a recovery can start, and drop the pin, during that
    // await, so a check made only beforehand would let a mutation through with
    // headers prepared against the tenant we just left (codex P1).
    for (let attempt = 0; ; attempt++) {
      if (gated) await awaitTenantRecovery();
      const headers =
        opts.preparedHeaders && !forceRefresh && !ignorePrepared
          ? opts.preparedHeaders
          : await authHeaders(input, init, forceRefresh);
      // Honour the latch as well as the in-flight slot: a recovery that starts
      // AND fails during header preparation leaves the slot empty but the
      // tenant unconfirmed, and an empty slot must not read as success
      // (codex P1).
      if (!gated || (!_tenantRecovery && !_tenantUnresolved)) {
        const identity = {
          pin: headers.get("X-Active-Workspace"),
          session: _impersonationSessionId,
        };
        // A retry must land where the original was aimed. If the tenant moved
        // between attempts, refuse rather than re-issue the caller's write
        // against a workspace they did not choose (codex P1).
        if (
          requireIdentity &&
          (identity.pin !== requireIdentity.pin || identity.session !== requireIdentity.session)
        ) {
          throw new TenantChangedError();
        }
        sentWith = identity;
        return fetch(input, { ...init, headers });
      }
      // The tenant moved while we were preparing. Go back and settle it rather
      // than sending headers built against the tenant we just left; give up
      // rather than spin.
      if (attempt >= 2) throw new TenantContextUnresolvedError();
    }
  };

  let r = await send(false);
  // The cached ID token may be stale (Firebase keeps it for an hour). One
  // forced refresh recovers the common case where the user came back to a
  // tab whose token has just expired.
  // The retry re-sends the SAME `init` — same body — after a forced refresh
  // that costs a round trip plus a token fetch. A caller whose body encodes a
  // time-sensitive decision must opt out (`retryOn401: false`) and re-decide on
  // its own next attempt, or it resends a verdict the clock has invalidated.
  if (r.status === 401 && firebaseAuth.currentUser && opts.retryOn401 !== false) {
    // The 403 recovery below deliberately retries under a CHANGED tenant (it
    // just dropped the pin); this one must not — it is re-sending the caller's
    // original request, body and all.
    r = await send(true, false, gated ? sentWith : undefined);
  }
  if (r.status === 401) {
    const { detail, body } = await readErrorBody(r);
    throw new AuthError(401, detail || "Not signed in", input, body);
  }
  if (r.status === 403) {
    r = await recoverWorkspaceDenied(r, init, sentWith, opts.tenantRecoveryProbe === true, send);
  }
  return r;
}

/** Methods with no side effects: safe to replay, and safe to let through
 * while a tenant recovery is in flight. */
const SIDE_EFFECT_FREE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

/**
 * Hold until any in-flight tenant re-resolution settles. A failed re-resolution
 * refuses the request rather than letting it out against an unknown tenant.
 */
async function awaitTenantRecovery(): Promise<void> {
  if (_tenantRecovery) {
    try {
      await _tenantRecovery;
      return;
    } catch {
      throw new TenantContextUnresolvedError();
    }
  }
  if (!_tenantUnresolved) return;
  // A previous re-resolution failed and none has succeeded since. Try once more
  // rather than soft-locking every write until a reload — but refuse if it
  // fails again, since the tenant is still unconfirmed.
  try {
    await notifyTenantContextChanged();
  } catch {
    throw new TenantContextUnresolvedError();
  }
}

/** Does this error body carry the server's stale/revoked-workspace code? */
function isWorkspaceDenied(body: unknown): boolean {
  const code = (body as { detail?: { code?: unknown } } | null)?.detail?.code;
  return code === "WORKSPACE_ACCESS_DENIED";
}

/**
 * WORKSPACE_ACCESS_DENIED recovery (ENG-4155 (c)) — recover rather than fail
 * closed, so the app lands on a working view instead of a blank thread.
 *
 * While impersonating, the old recovery (drop the pin, reload) was itself
 * destructive: the impersonation token lives only in memory, so the reload
 * ended the session it was trying to rescue. Instead drop the stale pin and
 * re-send the request exactly once, which resolves it against the impersonated
 * user's home org.
 *
 * The retry cannot loop. It fires only when `clearActiveWorkspacePin` reports
 * it actually removed a pin — so a 403 with no pin to drop retries zero times —
 * and its response is returned directly rather than re-entering this handler.
 *
 * **Only side-effect-free methods are ever replayed** (codex P1). The workspace
 * is resolved by an auth dependency that rejects before the handler runs, so
 * the denied request did not write anything — but replaying a POST/PATCH/DELETE
 * with the pin dropped would re-resolve it against the impersonated user's HOME
 * org and land the write in the wrong tenant, which is precisely the class of
 * failure ENG-1915 was filed for. A mutation instead fails closed with the pin
 * left intact, so the wire and stored state stay consistent and the caller
 * re-decides; the next read recovers the view.
 *
 * Outside impersonation the behavior is unchanged: drop the pin and reload, so
 * every pane re-resolves against a valid workspace rather than leaving the
 * chrome pointed at a workspace the user can no longer reach.
 */
async function recoverWorkspaceDenied(
  r: Response,
  init: RequestInit,
  sentWith: { pin: string | null; session: string | null },
  isRecoveryProbe: boolean,
  send: (forceRefresh: boolean, ignorePrepared?: boolean) => Promise<Response>,
): Promise<Response> {
  let body: unknown;
  try {
    body = await r.clone().json();
  } catch (e) {
    log.debug("403 body parse failed", e);
    return r;
  }
  if (!isWorkspaceDenied(body)) return r;
  if (!hasLiveImpersonation()) {
    recoverIfWorkspaceDenied(body);
    return r;
  }
  const method = (init.method ?? "GET").toUpperCase();
  if (!SIDE_EFFECT_FREE_METHODS.has(method)) {
    log.warn("workspace access denied while impersonating — not replaying a mutation", { method });
    return r;
  }
  if (!clearImpersonationWorkspacePinIfCurrent(sentWith)) return r;
  log.warn("workspace access denied while impersonating — dropped the pin, retrying once");
  // Wait for the app to re-resolve before retrying, so the response never lands
  // against a screen still describing the workspace we just left. Re-entry is
  // naturally deduped: the pin is already gone, so a concurrent 403 finds
  // nothing to drop and never announces again.
  // The re-resolution's OWN `/api/me` must not raise another resolution: it
  // would join the one waiting on this very request and hang both. Any other
  // request still needs to, even one that merely overlapped the handler.
  if (!isRecoveryProbe) {
    try {
      await notifyTenantContextChanged();
    } catch {
      // Tenant unknown — surface the original denial rather than retry blind.
      return r;
    }
  }
  return send(false, true);
}

/** Recover from a revoked/stale active workspace: when a response body carries
 * `detail.code === "WORKSPACE_ACCESS_DENIED"`, drop the pinned workspace and
 * reload so the user lands back in a valid context. Returns true when it
 * handled the case *and a reload is in flight* — callers rely on that to stop
 * work on a page that is going away. Used by the XHR uploader (uploads.ts);
 * `apiFetch` goes through `recoverWorkspaceDenied`, which can retry instead.
 *
 * Returns false while impersonating even though the pin is still dropped: a
 * reload would destroy the in-memory impersonation token (ENG-4155), so the
 * caller must surface the failure itself rather than wait for a navigation
 * that is never coming. */
export function recoverIfWorkspaceDenied(body: unknown): boolean {
  if (typeof window === "undefined") return false;
  if (!isWorkspaceDenied(body)) return false;
  // The remaining caller is the multipart uploader, i.e. always a mutation. So
  // while impersonating, leave the pin intact for exactly the reason
  // `recoverWorkspaceDenied` refuses to replay one: dropping it here would
  // silently rescope every later request to the impersonated user's HOME tenant
  // while the page still renders the old workspace, so re-attaching the file --
  // or any other workspace write -- would land in the wrong org (codex P1).
  // Failing closed with the pin untouched keeps the page and the wire agreeing.
  if (hasLiveImpersonation()) return false;
  clearActiveWorkspacePin();
  window.location.reload();
  return true;
}

/** Prefix a server-relative URL with the API origin. Returns the input
 * unchanged for absolute URLs (http/https/data). */
export function resolveApiUrl(path: string | null | undefined): string | null {
  if (!path) return null;
  if (/^(https?:|data:)/i.test(path)) return path;
  return `${API}${path}`;
}


// Generated from routes/memory.py. `metadata` was hand-rolled as
// `Record<string, unknown>` but the server emits exactly two keys:
// `size_bytes: int` and `updated_at: str` — now typed as MemoryMetadata.
export type MemoryDoc = Schemas["MemoryDoc"];
export type MemoryPreview = Schemas["MemoryPreview"];

// Generated from routes/activity.py:ActivityEntry. Hand-rolled version
// had topic/source/bytes optional and was missing `slug`; the server
// emits all 6 keys on every record.
export type ActivityEntry = Schemas["ActivityEntry"];

// Generated from routes/artifacts.py:ArtifactListing. The hand-rolled
// version had `display_name` and `tags` optional, but `list_artifacts()`
// always emits both (via parse_output_name, which returns `(list, str)`).
export type Artifact = Schemas["ArtifactListing"];

// Generated from routes/uploads.py:Upload. The hand-rolled version was
// missing `scope` (always emitted by the server) and had `project_id`
// as optional; the server emits it unconditionally as `string | null`.
export type Upload = Schemas["Upload"];
export type UploadsList = Schemas["UploadsList"];

export type ChatMessage = { role: "user" | "assistant"; text: string };

// Generated from routes/tasks.py:TaskSummary. The hand-rolled version
// declared `routine_id` / `routine_trigger`, but list_threads() does not
// actually include them in its row dicts — those live on the full Thread
// record (GET /api/tasks/{id}). Dropping them aligns the type with what
// the server actually returns.
export type TaskSummary = Schemas["TaskSummary"];

// All Task-tree types are generated from routes/tasks.py response models.
// The hand-rolled versions used the `path/name/display_name/tags/...`
// artifact shape inline in two places; that shape now lives in a shared
// `Artifact` schema. `in_progress` on TaskTurn — read by tasks.tsx during
// streaming — is now declared on the server model rather than cast at
// the call site.
export type TurnArtifact = Schemas["TurnArtifact"];
export type SliderProgress = Schemas["SliderProgress"];
export type ToolCallRecord = Schemas["ToolCallRecord"];
/**
 * Live-only extensions on a steer block for the Cursor-style queue
 * (ENG-3529). Never written by the server / never persisted — a reload
 * only ever sees plain SteerBlocks that already landed.
 */
export type SteerQueueStatus = "queued" | "failed";
export type ClientSteerBlock = Schemas["SteerBlock"] & {
  /** Stable id for optimistic reconcile against the SSE echo. */
  client_id?: string;
  /**
   * Delivery state while the client owns the interjection:
   * - `queued` — shown immediately on Enter; POST still in flight / waiting
   * - `failed` — POST rejected; user can retry
   * - absent — accepted (label "Steered"), matching a server-persisted block
   */
  queue_status?: SteerQueueStatus;
};

export type TurnBlock =
  | Schemas["TextBlock"]
  | Schemas["ToolCallBlock"]
  | ClientSteerBlock
  | Schemas["OutcomeBlock"]
  | Schemas["ArtifactsBlock"]
  // Client-only, never persisted: a live-preview draft of the coordinator's
  // next message (MA event_deltas). Always resolves into a real TextBlock
  // or is dropped before a turn finishes streaming — see `appendText` /
  // `clearPreview` in use-task-session.ts. `blocksFor()` never sees one on a
  // persisted turn, but task-turn.tsx's render switch needs a case for it
  // since it shares the `TurnBlock` union with the live view.
  | { type: "text_preview"; text: string };
export type TaskTurnAttachment = Schemas["TaskTurnAttachment"];
// Slides a user commented on (deck markup), persisted on the user turn / steer
// block so the transcript can render the commented slide as a thumbnail chip.
export type CommentSlideRef = Schemas["CommentSlideRef"];
export type TaskTurn = Schemas["TaskTurn"];
export type Task = Schemas["Task"];
// One entry on a task's agent-maintained checklist (CoWork-style).
export type TaskListItem = Schemas["TaskListItem"];

export type TaskRunStatus = Schemas["TaskRunStatus"];

export type AgentEvent =
  | { type: "stream_mode"; mode: "local" | "persisted" }
  | { type: "text"; text: string }
  // Live-preview text as the coordinator drafts an `agent.message` (MA
  // `event_deltas`). Best-effort and never final on its own — always
  // superseded by the "text" event for the same message, or explicitly
  // discarded via "text_delta_clear" if the draft never resolves into
  // renderable text (interrupted turn, tool-use-only response, etc.).
  // `index` is MA's `event_delta.delta.index` — a previewed message can
  // carry more than one content[] entry (e.g. two separate text blocks),
  // so the client accumulates per index rather than flat arrival order.
  | { type: "text_delta"; text: string; index: number }
  | { type: "text_delta_clear" }
  | { type: "tool_call"; name: string; input: Record<string, unknown>; tool_use_id?: string }
  | { type: "tool_result"; name: string; result: string; artifacts: Array<{ path: string; name?: string; display_name?: string; tags?: string[]; type: string; title: string; summary?: string }>; tool_use_id?: string; waiting?: boolean; confirm?: boolean; image?: { name: string; media_type?: string | null } }
  | { type: "tool_progress"; tool_use_id: string; tool_name?: string; progress: SliderProgress }
  | { type: "artifacts"; artifacts: Array<{ path: string; name?: string; display_name?: string; tags?: string[]; type: string; title: string; summary?: string }> }
  | { type: "status"; phase: string; label: string; done?: boolean }
  // One line of coordinator plan-position, produced in reply to a keepalive
  // ping while a delegated sub-agent runs (ENG-4351). Live-only and never
  // persisted: the backend broadcasts it but keeps it off the turn, so it
  // renders in the working footer beside the elapsed timer and leaves nothing
  // behind. Latest wins — N pings update one line rather than stacking N.
  | { type: "progress"; text: string }
  // Mid-turn credit-headroom notice (ENG-3104): "low" fires once per turn
  // when spendable headroom drops within one estimated turn; "exhausted"
  // fires once when the backend interrupts the turn. `last_error` (set at
  // turn end) carries the durable paused-state explanation — this event is
  // just the live, in-stream heads-up while it's still running.
  | { type: "credits"; status: "low" | "exhausted"; remaining_usd: number }
  | { type: "stop"; reason: string }
  | { type: "error"; message: string }
  | {
      type: "steer";
      text: string;
      comment_slides?: CommentSlideRef[] | null;
      // Files delivered into the live run with this steer (ENG-3222).
      attachments?: TaskTurnAttachment[] | null;
    }
  | {
      type: "outcome";
      phase: "start" | "ongoing" | "end";
      iteration: number;
      outcome_id?: string | null;
      result?: string | null;
      explanation?: string | null;
    }
  | { type: "title_updated"; title: string }
  | { type: "task_list"; task_list: TaskListItem[] }
  | {
      type: "sub_thread_event";
      thread_id: string;
      kind: string;
      payload: Record<string, unknown>;
    }
  | {
      // Authoritative per-worker completion from the sub-thread's own idle
      // status, cross-posted to the primary stream (ENG-2818). Flips the
      // matching sub_agent pill to done the moment ITS worker finishes,
      // independent of the coordinator's barrier relay. Keyed by
      // tool_use_id === session_thread_id.
      type: "sub_agent_status";
      tool_use_id: string;
      status: string;
    };


// Encode each path segment but keep "/" separators so the FastAPI path
// parameter sees the hierarchy intact.
function encodePath(p: string): string {
  return p.split("/").map(encodeURIComponent).join("/");
}

// Re-exported so per-resource sub-modules (./api/projects, etc.) can use
// the same encoder without duplicating it.
export { encodePath };


export type ChatStreamHandlers = {
  onEvent: (evt: AgentEvent) => void;
  onDone: () => void;
  onError: (err: string) => void;
  onReplayUnavailable?: () => void;
  /** Fired on every byte received from the stream, including SSE keepalive
   * comments (`: keepalive`) that carry no event. Lets the consumer prove the
   * *socket* is alive even when the agent is quiet (long tool / sub-agent
   * steps emit no events for minutes), so an idle watchdog can distinguish a
   * dead socket from a working-but-silent one and not churn reconnects. */
  onActivity?: () => void;
};

/** Distinguish a deliberate cancellation (component unmount, task switch,
 * timeout) from a real network/server failure. Browsers throw a DOMException
 * named "AbortError"; some surface it as a plain TypeError. Match both. */
export function isAbortError(e: unknown): boolean {
  if (!e || typeof e !== "object") return false;
  const name = (e as { name?: unknown }).name;
  return name === "AbortError";
}

/** Stream chat events using the legacy stateless endpoint. */
export async function streamChat(
  messages: ChatMessage[],
  handlers: ChatStreamHandlers,
  signal?: AbortSignal,
): Promise<void> {
  try {
    const r = await apiFetch(`${API}/api/chat`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
      body: JSON.stringify({ messages }),
      signal,
    });
    return await consumeSse(r, handlers);
  } catch (e) {
    if (isAbortError(e)) return;
    handlers.onError(errorMessage(e, String(e)));
  }
}

export async function consumeSse(r: Response, handlers: ChatStreamHandlers): Promise<void> {
  if (!r.ok || !r.body) {
    const { detail } = await readErrorBody(r);
    handlers.onError(detail);
    return;
  }
  const reader = r.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  while (true) {
    // reader.read() rejects with an AbortError when the request is
    // cancelled mid-stream (user switched tasks / unmounted). Let it
    // bubble — the caller's outer try/catch matches isAbortError and
    // suppresses onError for the intentional cancellation.
    const { value, done } = await reader.read();
    if (done) break;
    // Any byte — data frame OR a bare keepalive comment — proves the socket is
    // still live. Signal liveness before parsing so a long event-silent stretch
    // (held open by the server's 30s keepalive) doesn't look idle to the
    // watchdog and trigger a needless reconnect.
    handlers.onActivity?.();
    buf += decoder.decode(value, { stream: true });

    // Parse SSE: messages separated by \n\n; lines starting with "data:" carry the payload.
    let idx;
    while ((idx = buf.indexOf("\n\n")) !== -1) {
      const block = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      let event = "message";
      const dataLines: string[] = [];
      for (const line of block.split("\n")) {
        if (line.startsWith("event:")) event = line.slice(6).trim();
        else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
      }
      const dataStr = dataLines.join("\n");
      if (event === "done") {
        handlers.onDone();
        return;
      }
      if (!dataStr) continue;
      try {
        const evt = JSON.parse(dataStr) as AgentEvent;
        handlers.onEvent(evt);
      } catch (e) {
        // Malformed event from the server — log enough context to debug
        // (event name + a head of the payload) but keep the stream alive
        // so a single bad frame doesn't kill the session.
        log.warn("sse: malformed event payload", {
          event,
          preview: dataStr.slice(0, 200),
          error: errorMessage(e, String(e)),
        });
      }
    }
  }
  handlers.onDone();
}



// Per-resource sub-modules live under ./api/. Re-exported here so the
// "import from @/lib/api" convention every consumer uses continues to work.
// Each sub-file imports the HTTP plumbing (apiFetch, requestJson, etc.) and
// SSE helpers from this module, so the dependency graph is:
//
//   ./api/<resource>.ts  →  ./api.ts (this file)  ←  external callers
//
// The circular re-export is safe because every reference in the sub-files
// is in a function body, not module-load code.
export * from "./api/artifacts";
export * from "./api/connectors";
export * from "./api/decks";
export * from "./api/memory";
export * from "./api/projects";
export * from "./api/routines";
export * from "./api/skills";
export * from "./api/tasks";
export * from "./api/uploads";
export * from "./api/usage";
export * from "./api/org";
export * from "./api/workspaces";
